import { useState } from "react";
import { useKidContext } from "../layout";
import { useKidMissions, useClaimMission } from "@/hooks/use-kid-missions";
import { MissionCard } from "@/components/kid/mission-card";
import { Skeleton } from "@/components/ui/skeleton";
import type { Id } from "../../../../convex/_generated/dataModel";

export default function KidMissionsPage() {
  const { childId } = useKidContext();
  const { data, isLoading } = useKidMissions(childId);
  const { mutateAsync: claimMission, isPending: isClaiming } = useClaimMission();
  const [claimedThisSession, setClaimedThisSession] = useState<Set<string>>(new Set());

  async function handleClaim(missionId: string) {
    try {
      await claimMission({
        childId: childId as Id<"players">,
        missionId,
      });
      setClaimedThisSession((prev) => new Set(prev).add(missionId));
    } catch {
      // Silently ignore — most likely a duplicate claim
    }
  }

  if (isLoading) {
    return (
      <div className="px-6 pt-6 space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-56" />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-40 w-full rounded-2xl" />
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3 p-6 text-center">
        <div className="text-5xl">😕</div>
        <p className="text-lg font-semibold text-gray-700">Couldn't load missions</p>
      </div>
    );
  }

  const { activeMissions, completedCount, totalMissions, allCompleted } = data;

  return (
    <div className="px-4 pt-6 pb-4 space-y-4">
      <div className="px-2">
        <h2 className="text-2xl font-bold text-gray-900">My Missions</h2>
        <p className="text-sm text-gray-500 mt-1">
          {completedCount} of {totalMissions} completed
        </p>
      </div>

      {allCompleted ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
          <span className="text-6xl">🏆</span>
          <h3 className="text-xl font-bold text-gray-800">All missions complete!</h3>
          <p className="text-gray-500 text-sm">More missions coming soon. You're amazing!</p>
        </div>
      ) : activeMissions.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
          <span className="text-6xl">🎾</span>
          <p className="text-gray-500 text-sm">No missions available right now.</p>
        </div>
      ) : (
        activeMissions.map((mission) => (
          <MissionCard
            key={mission.id}
            missionId={mission.id}
            title={mission.title}
            description={mission.description}
            emoji={mission.emoji}
            points={mission.points}
            isPending={mission.isPending || claimedThisSession.has(mission.id)}
            childId={childId}
            onClaim={handleClaim}
            isClaiming={isClaiming}
          />
        ))
      )}

      {completedCount > 0 && !allCompleted && (
        <p className="text-center text-xs text-gray-400 pt-2">
          🎉 {completedCount} mission{completedCount !== 1 ? "s" : ""} completed!
        </p>
      )}
    </div>
  );
}
